import {
  ArrowLeft as LucideArrowLeft,
  AudioWaveform as LucideAudioWaveform,
  Calendar as LucideCalendar,
  Check as LucideCheck,
  CheckCircle as LucideCheckCircle,
  CheckCircle2 as LucideCheckCircle2,
  ChevronDown as LucideChevronDown,
  ChevronRight as LucideChevronRight,
  ChevronUp as LucideChevronUp,
  ChevronsDownUp as LucideChevronsDownUp,
  ChevronsLeftRight as LucideChevronsLeftRight,
  ChevronsUpDown as LucideChevronsUpDown,
  Circle as LucideCircle,
  CircleCheck as LucideCircleCheck,
  Clock as LucideClock,
  Copy as LucideCopy,
  CreditCard as LucideCreditCard,
  DollarSign as LucideDollarSign,
  Download as LucideDownload,
  ExternalLink as LucideExternalLink,
  FileDown as LucideFileDown,
  FileJson as LucideFileJson,
  Flag as LucideFlag,
  FolderOpen as LucideFolderOpen,
  Gauge as LucideGauge,
  Gavel as LucideGavel,
  Heart as LucideHeart,
  KeyRound as LucideKeyRound,
  Layers as LucideLayers,
  LayoutDashboard as LucideLayoutDashboard,
  Link2 as LucideLink2,
  Lock as LucideLock,
  LogOut as LucideLogOut,
  MapPin as LucideMapPin,
  Maximize2 as LucideMaximize2,
  Menu as LucideMenu,
  MessageSquare as LucideMessageSquare,
  Mic as LucideMic,
  Music as LucideMusic,
  Music2 as LucideMusic2,
  Package as LucidePackage,
  PanelLeft as LucidePanelLeft,
  Pause as LucidePause,
  Pencil as LucidePencil,
  Pin as LucidePin,
  Play as LucidePlay,
  Plus as LucidePlus,
  Redo2 as LucideRedo2,
  RefreshCw as LucideRefreshCw,
  Repeat2 as LucideRepeat2,
  Reply as LucideReply,
  RotateCcw as LucideRotateCcw,
  Route as LucideRoute,
  Save as LucideSave,
  Scissors as LucideScissors,
  Search as LucideSearch,
  Send as LucideSend,
  Settings as LucideSettings,
  ShieldCheck as LucideShieldCheck,
  SkipBack as LucideSkipBack,
  SkipForward as LucideSkipForward,
  SlidersHorizontal as LucideSlidersHorizontal,
  SlidersVertical as LucideSlidersVertical,
  Square as LucideSquare,
  StretchHorizontal as LucideStretchHorizontal,
  StretchVertical as LucideStretchVertical,
  TimerReset as LucideTimerReset,
  Trash2 as LucideTrash2,
  Undo2 as LucideUndo2,
  Upload as LucideUpload,
  UserRoundCheck as LucideUserRoundCheck,
  X as LucideX,
  Zap as LucideZap,
  ZoomIn as LucideZoomIn,
  ZoomOut as LucideZoomOut,
  type LucideProps,
} from "lucide-react";
import { createContext, useContext, useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { useAppearance, type IconLibrary } from "./appearance";

type IconComponent = ComponentType<LucideProps & { isAnimated?: boolean; weight?: string }>;
type IconModule = Record<string, IconComponent | undefined>;

const LUCIDE_ICONS: IconModule = {
  ArrowLeft: LucideArrowLeft,
  AudioWaveform: LucideAudioWaveform,
  Calendar: LucideCalendar,
  Check: LucideCheck,
  CheckCircle: LucideCheckCircle,
  CheckCircle2: LucideCheckCircle2,
  ChevronDown: LucideChevronDown,
  ChevronRight: LucideChevronRight,
  ChevronUp: LucideChevronUp,
  ChevronsDownUp: LucideChevronsDownUp,
  ChevronsLeftRight: LucideChevronsLeftRight,
  ChevronsUpDown: LucideChevronsUpDown,
  Circle: LucideCircle,
  CircleCheck: LucideCircleCheck,
  Clock: LucideClock,
  Copy: LucideCopy,
  CreditCard: LucideCreditCard,
  DollarSign: LucideDollarSign,
  Download: LucideDownload,
  ExternalLink: LucideExternalLink,
  FileDown: LucideFileDown,
  FileJson: LucideFileJson,
  Flag: LucideFlag,
  FolderOpen: LucideFolderOpen,
  Gauge: LucideGauge,
  Gavel: LucideGavel,
  Heart: LucideHeart,
  KeyRound: LucideKeyRound,
  Layers: LucideLayers,
  LayoutDashboard: LucideLayoutDashboard,
  Link2: LucideLink2,
  Lock: LucideLock,
  LogOut: LucideLogOut,
  MapPin: LucideMapPin,
  Maximize2: LucideMaximize2,
  Menu: LucideMenu,
  MessageSquare: LucideMessageSquare,
  Mic: LucideMic,
  Music: LucideMusic,
  Music2: LucideMusic2,
  Package: LucidePackage,
  PanelLeft: LucidePanelLeft,
  Pause: LucidePause,
  Pencil: LucidePencil,
  Pin: LucidePin,
  Play: LucidePlay,
  Plus: LucidePlus,
  Redo2: LucideRedo2,
  RefreshCw: LucideRefreshCw,
  Repeat2: LucideRepeat2,
  Reply: LucideReply,
  RotateCcw: LucideRotateCcw,
  Route: LucideRoute,
  Save: LucideSave,
  Scissors: LucideScissors,
  Search: LucideSearch,
  Send: LucideSend,
  Settings: LucideSettings,
  ShieldCheck: LucideShieldCheck,
  SkipBack: LucideSkipBack,
  SkipForward: LucideSkipForward,
  SlidersHorizontal: LucideSlidersHorizontal,
  SlidersVertical: LucideSlidersVertical,
  Square: LucideSquare,
  StretchHorizontal: LucideStretchHorizontal,
  StretchVertical: LucideStretchVertical,
  TimerReset: LucideTimerReset,
  Trash2: LucideTrash2,
  Undo2: LucideUndo2,
  Upload: LucideUpload,
  UserRoundCheck: LucideUserRoundCheck,
  X: LucideX,
  Zap: LucideZap,
  ZoomIn: LucideZoomIn,
  ZoomOut: LucideZoomOut,
};

const LOADERS: Partial<Record<IconLibrary, () => Promise<IconModule>>> = {
  tabler: () => import("react-icons/tb") as unknown as Promise<IconModule>,
  phosphor: () => import("@phosphor-icons/react") as unknown as Promise<IconModule>,
  remix: () => import("react-icons/ri") as unknown as Promise<IconModule>,
  fontawesome: () => import("react-icons/fa") as unknown as Promise<IconModule>,
  reicon: () => import("reicon-react") as unknown as Promise<IconModule>,
  animateicons: () => import("@animateicons/react/lucide") as unknown as Promise<IconModule>,
};

const ALIASES: Record<Exclude<IconLibrary, "lucide">, Record<string, string>> = {
  tabler: {
    AudioWaveform: "TbWaveSine", CheckCircle: "TbCircleCheck", CheckCircle2: "TbCircleCheck",
    CircleCheck: "TbCircleCheck", DollarSign: "TbCurrencyDollar", FileDown: "TbFileDownload",
    FileJson: "TbFileTypeJson", KeyRound: "TbKey", LayoutDashboard: "TbLayoutDashboard",
    Link2: "TbLink", LogOut: "TbLogout", Maximize2: "TbMaximize", MessageSquare: "TbMessage",
    Mic: "TbMicrophone", Music2: "TbMusic", PanelLeft: "TbLayoutSidebarLeftExpand",
    Redo2: "TbArrowForwardUp", Repeat2: "TbRepeat", RotateCcw: "TbRotateCounterclockwise",
    Save: "TbDeviceFloppy", ShieldCheck: "TbShieldCheck", SkipBack: "TbPlayerSkipBack",
    SkipForward: "TbPlayerSkipForward", TimerReset: "TbRestore", Trash2: "TbTrash",
    Undo2: "TbArrowBackUp", UserRoundCheck: "TbUserCheck", Zap: "TbBolt",
    StretchHorizontal: "TbArrowsHorizontal", StretchVertical: "TbArrowsVertical",
  },
  phosphor: {
    AudioWaveform: "Waveform", ChevronDown: "CaretDown", ChevronRight: "CaretRight", ChevronUp: "CaretUp",
    ChevronsDownUp: "CaretUpDown", ChevronsUpDown: "CaretUpDown", CircleCheck: "CheckCircle",
    DollarSign: "CurrencyDollar", FileDown: "FileArrowDown", FileJson: "FileCode", KeyRound: "Key",
    LayoutDashboard: "SquaresFour", Link2: "Link", LogOut: "SignOut", Maximize2: "ArrowsOut",
    MessageSquare: "ChatSquare", Mic: "Microphone", Music2: "MusicNote", PanelLeft: "SidebarSimple",
    Redo2: "ArrowBendUpRight", Repeat2: "Repeat", RotateCcw: "ArrowCounterClockwise", Save: "FloppyDisk",
    SlidersHorizontal: "SlidersHorizontal", SlidersVertical: "Sliders", TimerReset: "Timer",
    Trash2: "Trash", Undo2: "ArrowBendUpLeft", UserRoundCheck: "UserCircleCheck", Zap: "Lightning",
    StretchHorizontal: "ArrowsOutLineHorizontal", StretchVertical: "ArrowsOutLineVertical",
  },
  remix: {
    AudioWaveform: "RiPulseLine", CheckCircle: "RiCheckboxCircleLine", CheckCircle2: "RiCheckboxCircleLine",
    CircleCheck: "RiCheckboxCircleLine", ChevronDown: "RiArrowDownSLine", ChevronRight: "RiArrowRightSLine",
    ChevronUp: "RiArrowUpSLine", ChevronsDownUp: "RiContractUpDownLine", ChevronsUpDown: "RiExpandUpDownLine",
    DollarSign: "RiMoneyDollarCircleLine", FileDown: "RiFileDownloadLine", FileJson: "RiFileCodeLine",
    FolderOpen: "RiFolderOpenLine", KeyRound: "RiKey2Line", LayoutDashboard: "RiDashboardLine",
    Link2: "RiLink", LogOut: "RiLogoutBoxRLine", Maximize2: "RiFullscreenLine", MessageSquare: "RiChat1Line",
    Mic: "RiMicLine", Music2: "RiMusic2Line", Package: "RiArchiveLine", PanelLeft: "RiSideBarLine",
    Redo2: "RiArrowGoForwardLine", Repeat2: "RiRepeat2Line", RotateCcw: "RiRestartLine", Save: "RiSave3Line",
    ShieldCheck: "RiShieldCheckLine", SkipBack: "RiSkipBackLine", SkipForward: "RiSkipForwardLine",
    SlidersHorizontal: "RiEqualizer2Line", SlidersVertical: "RiEqualizerLine", TimerReset: "RiTimerLine",
    Trash2: "RiDeleteBinLine", Undo2: "RiArrowGoBackLine", UserRoundCheck: "RiUserFollowLine", Zap: "RiFlashlightLine",
    StretchHorizontal: "RiExpandHorizontalLine", StretchVertical: "RiExpandVerticalLine",
  },
  fontawesome: {
    AudioWaveform: "FaWaveSquare", CheckCircle2: "FaCheckCircle", CircleCheck: "FaCheckCircle",
    ChevronDown: "FaChevronDown", ChevronRight: "FaChevronRight", ChevronUp: "FaChevronUp",
    ChevronsDownUp: "FaExchangeAlt", ChevronsUpDown: "FaExchangeAlt", DollarSign: "FaDollarSign",
    FileDown: "FaFileDownload", FileJson: "FaFileCode", FolderOpen: "FaFolderOpen", KeyRound: "FaKey",
    LayoutDashboard: "FaThLarge", Link2: "FaLink", LogOut: "FaSignOutAlt", Maximize2: "FaExpand",
    MessageSquare: "FaCommentAlt", Mic: "FaMicrophone", Music2: "FaMusic", Package: "FaArchive",
    PanelLeft: "FaColumns", Redo2: "FaRedo", Repeat2: "FaRepeat", RotateCcw: "FaUndo",
    Save: "FaSave", ShieldCheck: "FaShieldAlt", SkipBack: "FaStepBackward", SkipForward: "FaStepForward",
    SlidersHorizontal: "FaSlidersH", SlidersVertical: "FaSlidersV", TimerReset: "FaStopwatch",
    Trash2: "FaTrashAlt", Undo2: "FaUndo", UserRoundCheck: "FaUserCheck", X: "FaTimes", Zap: "FaBolt",
    StretchHorizontal: "FaArrowsAltH", StretchVertical: "FaArrowsAltV",
  },
  reicon: {
    AudioWaveform: "Pulse", CheckCircle: "CheckCircle", CheckCircle2: "CheckCircle", CircleCheck: "CheckCircle",
    ChevronDown: "ChevronDown", ChevronRight: "ChevronRight", ChevronUp: "ChevronUp",
    ChevronsDownUp: "ChevronExpandY", ChevronsUpDown: "ChevronExpandY", DollarSign: "DollarCircle",
    FileDown: "FileDownload", FileJson: "FileCode", KeyRound: "Key", LayoutDashboard: "Layout",
    Link2: "Link2", LogOut: "Logout", Maximize2: "Fullscreen", MessageSquare: "ChatSquare",
    Mic: "Microphone", Music2: "Music", Package: "ArchiveBox", PanelLeft: "LayoutSidebarLeft",
    Redo2: "Redo", Repeat2: "Repeat", RotateCcw: "RotateCcw", Save: "Floppy",
    ShieldCheck: "ShieldCheck", SkipBack: "SkipBack", SkipForward: "SkipForward",
    SlidersHorizontal: "SlidersHorizontal", SlidersVertical: "SlidersVertical", TimerReset: "TimerReset",
    Trash2: "Trash", Undo2: "Undo", UserRoundCheck: "UserCheck", Zap: "Lightning",
    StretchHorizontal: "ArrowsHorizontal", StretchVertical: "ArrowsVertical",
  },
  animateicons: {
    AudioWaveform: "AudioLinesIcon", CheckCircle: "CircleCheckIcon", CheckCircle2: "CircleCheckIcon",
    CircleCheck: "CircleCheckIcon", LayoutDashboard: "LayoutDashboardIcon", LogOut: "LogOutIcon",
    Music2: "MusicIcon", Redo2: "Redo2Icon", RotateCcw: "RotateCcwIcon", Undo2: "Undo2Icon",
  },
};

const IconModuleContext = createContext<IconModule>(LUCIDE_ICONS);
const moduleCache = new Map<IconLibrary, IconModule>([["lucide", LUCIDE_ICONS]]);

function defaultExportName(library: IconLibrary, name: string): string {
  if (library === "tabler") return `Tb${name}`;
  if (library === "remix") return `Ri${name}Line`;
  if (library === "fontawesome") return `Fa${name}`;
  if (library === "animateicons") return `${name}Icon`;
  return name;
}

export function IconProvider({ children }: { children: ReactNode }) {
  const { iconLibrary } = useAppearance();
  const [icons, setIcons] = useState<IconModule>(() => moduleCache.get(iconLibrary) ?? LUCIDE_ICONS);

  useEffect(() => {
    let cancelled = false;
    const cached = moduleCache.get(iconLibrary);
    if (cached) {
      setIcons(cached);
      return () => { cancelled = true; };
    }
    setIcons(LUCIDE_ICONS);
    const loader = LOADERS[iconLibrary];
    if (!loader) return () => { cancelled = true; };
    void loader().then((module) => {
      moduleCache.set(iconLibrary, module);
      if (!cancelled) setIcons(module);
    }).catch(() => {
      if (!cancelled) setIcons(LUCIDE_ICONS);
    });
    return () => { cancelled = true; };
  }, [iconLibrary]);

  const value = useMemo(() => icons, [icons]);
  return <IconModuleContext.Provider value={value}>{children}</IconModuleContext.Provider>;
}

function makeIcon(name: string): IconComponent {
  function PortalIcon(props: LucideProps) {
    const module = useContext(IconModuleContext);
    const { iconLibrary } = useAppearance();
    const alias = iconLibrary === "lucide" ? name : ALIASES[iconLibrary]?.[name];
    const exportName = alias ?? defaultExportName(iconLibrary, name);
    const LibraryIcon = module[exportName];
    const SelectedIcon = LibraryIcon ?? LUCIDE_ICONS[name] ?? LucideCircle;
    const libraryProps = LibraryIcon && iconLibrary === "phosphor"
      ? { weight: "regular" }
      : LibraryIcon && iconLibrary === "animateicons"
        ? { isAnimated: true }
        : {};
    return <SelectedIcon {...props} {...libraryProps} />;
  }
  PortalIcon.displayName = `Portal${name}`;
  return PortalIcon;
}

export const ArrowLeft = makeIcon("ArrowLeft");
export const AudioWaveform = makeIcon("AudioWaveform");
export const Calendar = makeIcon("Calendar");
export const Check = makeIcon("Check");
export const CheckCircle = makeIcon("CheckCircle");
export const CheckCircle2 = makeIcon("CheckCircle2");
export const ChevronDown = makeIcon("ChevronDown");
export const ChevronRight = makeIcon("ChevronRight");
export const ChevronUp = makeIcon("ChevronUp");
export const ChevronsDownUp = makeIcon("ChevronsDownUp");
export const ChevronsLeftRight = makeIcon("ChevronsLeftRight");
export const ChevronsUpDown = makeIcon("ChevronsUpDown");
export const Circle = makeIcon("Circle");
export const CircleCheck = makeIcon("CircleCheck");
export const Clock = makeIcon("Clock");
export const Copy = makeIcon("Copy");
export const CreditCard = makeIcon("CreditCard");
export const DollarSign = makeIcon("DollarSign");
export const Download = makeIcon("Download");
export const ExternalLink = makeIcon("ExternalLink");
export const FileDown = makeIcon("FileDown");
export const FileJson = makeIcon("FileJson");
export const Flag = makeIcon("Flag");
export const FolderOpen = makeIcon("FolderOpen");
export const Gauge = makeIcon("Gauge");
export const Gavel = makeIcon("Gavel");
export const Heart = makeIcon("Heart");
export const KeyRound = makeIcon("KeyRound");
export const Layers = makeIcon("Layers");
export const LayoutDashboard = makeIcon("LayoutDashboard");
export const Link2 = makeIcon("Link2");
export const Lock = makeIcon("Lock");
export const LogOut = makeIcon("LogOut");
export const MapPin = makeIcon("MapPin");
export const Maximize2 = makeIcon("Maximize2");
export const Menu = makeIcon("Menu");
export const MessageSquare = makeIcon("MessageSquare");
export const Mic = makeIcon("Mic");
export const Music = makeIcon("Music");
export const Music2 = makeIcon("Music2");
export const Package = makeIcon("Package");
export const PanelLeft = makeIcon("PanelLeft");
export const Pause = makeIcon("Pause");
export const Pencil = makeIcon("Pencil");
export const Pin = makeIcon("Pin");
export const Play = makeIcon("Play");
export const Plus = makeIcon("Plus");
export const Redo2 = makeIcon("Redo2");
export const RefreshCw = makeIcon("RefreshCw");
export const Repeat2 = makeIcon("Repeat2");
export const Reply = makeIcon("Reply");
export const RotateCcw = makeIcon("RotateCcw");
export const Route = makeIcon("Route");
export const Save = makeIcon("Save");
export const Scissors = makeIcon("Scissors");
export const Search = makeIcon("Search");
export const Send = makeIcon("Send");
export const Settings = makeIcon("Settings");
export const ShieldCheck = makeIcon("ShieldCheck");
export const SkipBack = makeIcon("SkipBack");
export const SkipForward = makeIcon("SkipForward");
export const SlidersHorizontal = makeIcon("SlidersHorizontal");
export const SlidersVertical = makeIcon("SlidersVertical");
export const Square = makeIcon("Square");
export const StretchHorizontal = makeIcon("StretchHorizontal");
export const StretchVertical = makeIcon("StretchVertical");
export const TimerReset = makeIcon("TimerReset");
export const Trash2 = makeIcon("Trash2");
export const Undo2 = makeIcon("Undo2");
export const Upload = makeIcon("Upload");
export const UserRoundCheck = makeIcon("UserRoundCheck");
export const X = makeIcon("X");
export const Zap = makeIcon("Zap");
export const ZoomIn = makeIcon("ZoomIn");
export const ZoomOut = makeIcon("ZoomOut");
